import {IfcRelationModel} from './ifc-relations';

/**
 * Properties der IFC-Elemente über die Fragments-Daten – Grundlage für chatBICs Abfragen und Filter.
 *
 * <p>Form der Daten, wie sie der Fragments-Importer liefert (an echten .frag-Dateien geprüft):
 *
 * <ul>
 *   <li>Am Element hängen unter `IsDefinedBy` die Eigenschaftssätze (`IFCPROPERTYSET` mit
 *       `HasProperties` → `Name`/`NominalValue`), die Mengen (`IFCELEMENTQUANTITY` mit `Quantities`
 *       → `Name`/`LengthValue`, `AreaValue` …) und – nur bei neueren Konvertierungen – das Typobjekt
 *       (`…TYPE`) mit seinen eigenen Sätzen unter `HasPropertySets`.</li>
 *   <li>Eine Property ohne Namen kommt vor (`_category: null`) und wird übersprungen.</li>
 * </ul>
 *
 * <p>Die Abfrage nennt jede Relation ausdrücklich. Mit `relations: true` ohne Begrenzung lädt die
 * Engine rekursiv weiter – über `DefinesOcurrence` zurück zu allen Elementen desselben Satzes –
 * und hing an einer 600-KB-Datei minutenlang.
 */

/** Ein Property eines Elements; `pset` ist der Satzname, Attribute stehen unter {@link ATTRIBUTE_PSET}. */
export interface IfcPropertyRow {
  pset: string;
  property: string;
  value: string | number | boolean;
}

/** Satzname der Element-Attribute und der Pseudo-Properties (IFC-Klasse, Typ, Geschoss). */
export const ATTRIBUTE_PSET = 'Attribute';

export type PropertyOperator = 'eq' | 'neq' | 'contains' | 'gt' | 'gte' | 'lt' | 'lte' | 'exists' | 'missing';

/** Eine Filterbedingung von chatBIC, siehe `ChatViewerTools#where` im Backend. */
export interface PropertyCondition {
  pset?: string;
  property: string;
  op: PropertyOperator;
  value?: string;
}

/** Attribute des Elements, die wie Properties abfragbar sind. */
const ELEMENT_ATTRIBUTES = ['Name', 'ObjectType', 'Tag', 'PredefinedType', 'Description', 'LongName'];

const TYPE_CATEGORY = /(TYPE|STYLE)$/i;

/** Blockgröße für `getItemsData` – hält einzelne Aufrufe auch bei großen Modellen kurz. */
const CHUNK = 2000;

/**
 * `attributesDefault: true` ist Pflicht: eine `attributes`-Liste gilt auch für die verknüpften
 * Objekte – mit ihr kamen die Sätze ohne `HasProperties`, `NominalValue` und Mengenwerte zurück.
 */
const PROPERTY_DATA_CONFIG = {
  attributesDefault: true,
  relations: {
    IsDefinedBy: {attributes: true, relations: true},
    HasPropertySets: {attributes: true, relations: true},
    HasProperties: {attributes: true, relations: false},
    Quantities: {attributes: true, relations: false},
    HasQuantities: {attributes: true, relations: false},
    DefinesOcurrence: {attributes: false, relations: false},
  },
  relationsDefault: {attributes: false, relations: false},
};

const raw = (attribute: any): any =>
  attribute && typeof attribute === 'object' && 'value' in attribute ? attribute.value : attribute;

/** Rundet Gleitkomma-Rauschen weg (11399.999999999942 → 11400), ohne echte Nachkommastellen zu verlieren. */
const tidy = (value: any): string | number | boolean | undefined => {
  if (value == null) return undefined;
  if (typeof value === 'number') return Math.round(value * 1e6) / 1e6;
  if (typeof value === 'boolean') return value;
  if (Array.isArray(value)) return value.map(v => String(raw(v))).join(', ');
  const text = String(value).trim();
  return text === '' ? undefined : text;
};

/**
 * Die Properties je Element.
 *
 * <p>Die leere Liste ist abgefangen: `getItemsData([])` liefert nicht nichts, sondern ALLE Items.
 */
export async function propertyRows(model: IfcRelationModel, localIds: number[]): Promise<Map<number, IfcPropertyRow[]>> {
  const result = new Map<number, IfcPropertyRow[]>();
  for (let start = 0; start < localIds.length; start += CHUNK) {
    const chunk = localIds.slice(start, start + CHUNK);
    const data = await model.getItemsData(chunk, PROPERTY_DATA_CONFIG);
    data.forEach((item: any, i: number) => {
      const localId = raw(item?._localId) ?? chunk[i];
      result.set(localId, flattenItem(item));
    });
  }
  return result;
}

/** Flacht ein Element ab. Die Sätze des Elements gehen denen seines Typs vor. */
export function flattenItem(item: any): IfcPropertyRow[] {
  const rows: IfcPropertyRow[] = [];
  const seen = new Set<string>();
  const add = (pset: string, property: string, value: any) => {
    const tidied = tidy(value);
    if (tidied === undefined) return;
    const key = `${pset.toLowerCase()}\u0000${property.toLowerCase()}`;
    if (seen.has(key)) return;
    seen.add(key);
    rows.push({pset, property, value: tidied});
  };

  const category = raw(item?._category);
  if (category) add(ATTRIBUTE_PSET, 'IFC-Klasse', String(category));
  for (const attribute of ELEMENT_ATTRIBUTES) add(ATTRIBUTE_PSET, attribute, raw(item?.[attribute]));

  const definitions: any[] = item?.IsDefinedBy ?? [];
  const typeObjects = definitions.filter(d => TYPE_CATEGORY.test(String(raw(d?._category) ?? '')));
  for (const set of definitions.filter(d => !typeObjects.includes(d))) flattenSet(set, add);
  for (const type of typeObjects) {
    for (const set of type?.HasPropertySets ?? []) flattenSet(set, add);
  }
  return rows;
}

function flattenSet(set: any, add: (pset: string, property: string, value: any) => void): void {
  const name = raw(set?.Name);
  const pset = name != null && String(name).trim() !== '' ? String(name) : String(raw(set?._category) ?? 'Pset');

  for (const property of set?.HasProperties ?? []) {
    const propertyName = raw(property?.Name);
    if (!propertyName) continue;
    const value = raw(property?.NominalValue) ?? raw(property?.EnumerationValues) ?? raw(property?.ListValues);
    add(pset, String(propertyName), value);
  }
  for (const quantity of [...(set?.Quantities ?? []), ...(set?.HasQuantities ?? [])]) {
    const quantityName = raw(quantity?.Name);
    if (!quantityName) continue;
    // Die Wert-Spalte heißt je Mengenart anders: LengthValue, AreaValue, VolumeValue, CountValue …
    const valueKey = Object.keys(quantity).find(key => key.endsWith('Value'));
    add(pset, String(quantityName), valueKey ? raw(quantity[valueKey]) : undefined);
  }
}

const same = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

/** Die Zeilen eines Properties; ohne `pset` aus jedem Satz. */
export function rowsOf(rows: IfcPropertyRow[], property: string, pset?: string): IfcPropertyRow[] {
  return rows.filter(row => same(row.property, property) && (!pset || same(row.pset, pset)));
}

/** Zahl aus einem Property-Wert – auch „0,41" oder „12.5"; sonst `undefined`. */
export function asNumber(value: unknown): number | undefined {
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
  if (typeof value !== 'string') return undefined;
  const text = value.trim();
  if (!/^[-+]?\d+([.,]\d+)?([eE][-+]?\d+)?$/.test(text)) return undefined;
  return Number(text.replace(',', '.'));
}

function asBoolean(value: unknown): boolean | undefined {
  if (typeof value === 'boolean') return value;
  const text = String(value).trim().toLowerCase();
  if (['true', 'wahr', 'ja', 'yes', '.t.'].includes(text)) return true;
  if (['false', 'falsch', 'nein', 'no', '.f.'].includes(text)) return false;
  return undefined;
}

function equals(actual: IfcPropertyRow['value'], wanted: string): boolean {
  const numbers = [asNumber(actual), asNumber(wanted)];
  if (numbers[0] !== undefined && numbers[1] !== undefined) {
    return Math.abs(numbers[0] - numbers[1]) <= 1e-9 * Math.max(1, Math.abs(numbers[1]));
  }
  if (typeof actual === 'boolean') return asBoolean(wanted) === actual;
  return same(String(actual), wanted);
}

function compare(actual: IfcPropertyRow['value'], op: PropertyOperator, wanted: string): boolean {
  switch (op) {
    case 'eq':
      return equals(actual, wanted);
    case 'neq':
      return !equals(actual, wanted);
    case 'contains':
      return String(actual).toLowerCase().includes(wanted.trim().toLowerCase());
    default: {
      const a = asNumber(actual);
      const b = asNumber(wanted);
      if (a === undefined || b === undefined) return false;
      if (op === 'gt') return a > b;
      if (op === 'gte') return a >= b;
      if (op === 'lt') return a < b;
      return a <= b;
    }
  }
}

/** Ob ein Element eine Bedingung erfüllt. `neq` verlangt, dass es das Property überhaupt hat. */
export function matches(rows: IfcPropertyRow[], condition: PropertyCondition): boolean {
  const candidates = rowsOf(rows, condition.property, condition.pset);
  if (condition.op === 'exists') return candidates.length > 0;
  if (condition.op === 'missing') return candidates.length === 0;
  if (condition.value == null) return false;
  return candidates.some(row => compare(row.value, condition.op, condition.value!));
}

/** Ob ein Element alle Bedingungen erfüllt. */
export function matchesAll(rows: IfcPropertyRow[], conditions: PropertyCondition[]): boolean {
  return conditions.every(condition => matches(rows, condition));
}
